import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ImportModal } from "../components/ImportModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Tag, Search, X, Upload } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const TYPES = [
    { v: "buyer", label: "Buyer" },
    { v: "seller", label: "Seller" },
    { v: "intermediary", label: "Intermediary" },
    { v: "investor", label: "Investor" },
];

export default function Contacts() {
    const [contacts, setContacts] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [importOpen, setImportOpen] = useState(false);
    const [warmthScores, setWarmthScores] = useState({});
    const [referralStats, setReferralStats] = useState({});

    const refresh = async () => {
        const { data } = await api.get("/contacts");
        setContacts(data);
    };
    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, []);

    // After contacts load: batch-fetch warmth scores + top-referrers
    useEffect(() => {
        if (!contacts.length) return;
        const buyerIds = contacts.filter(c => c.type === "buyer" || c.type === "investor").map(c => c.id);
        if (buyerIds.length > 0) {
            api.get(`/contacts/warmth-scores?ids=${buyerIds.join(",")}`)
                .then(r => setWarmthScores(r.data || {}))
                .catch(() => {});
        }
        api.get("/contacts/top-referrers")
            .then(r => {
                const map = {};
                (r.data || []).forEach(ref => { map[ref.contact_id] = ref; });
                setReferralStats(map);
            })
            .catch(() => {});
    }, [contacts]);

    const q = search.trim().toLowerCase();
    const filtered = contacts
        .filter((c) => filter === "all" || c.type === filter)
        .filter((c) =>
            !q ||
            (c.name || "").toLowerCase().includes(q) ||
            (c.company || "").toLowerCase().includes(q)
        );
    const isFiltering = filter !== "all" || !!q;

    const del = async (id) => {
        if (!confirm("Delete contact?")) return;
        await api.delete(`/contacts/${id}`);
        refresh();
    };

    return (
        <div data-testid="contacts-page">
            <PageHeader
                title="Contacts"
                subtitle={isFiltering
                    ? `${filtered.length} of ${contacts.length} parties in your network`
                    : `${contacts.length} parties in your network`}
            >
                <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name or company…"
                        className="h-9 w-64 pl-8 pr-8"
                        data-testid="contacts-search-input"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            data-testid="contacts-search-clear"
                            aria-label="Clear search"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-contacts-btn">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Import
                </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="new-contact-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> New Contact
                        </Button>
                    </DialogTrigger>
                    <NewContactDialog onCreated={() => { setOpen(false); refresh(); }} />
                </Dialog>
            </PageHeader>
            <ImportModal open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) refresh(); }} defaultTab="contacts" />

            <div className="p-6 lg:p-8">
                <Tabs value={filter} onValueChange={setFilter} className="mb-4">
                    <TabsList data-testid="contact-filter-tabs" className="bg-transparent border-b border-[#E2E8F0] rounded-none h-auto p-0 gap-0 justify-start w-auto">
                        <TabsTrigger value="all" className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#0B1F3A] data-[state=active]:text-[#0B1F3A] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:shadow-none text-[#64748B] text-[13px] px-3 py-2 -mb-px h-auto">All</TabsTrigger>
                        {TYPES.map((t) => <TabsTrigger key={t.v} value={t.v} className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#0B1F3A] data-[state=active]:text-[#0B1F3A] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:shadow-none text-[#64748B] text-[13px] px-3 py-2 -mb-px h-auto">{t.label}s</TabsTrigger>)}
                    </TabsList>
                </Tabs>

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md p-12 text-center" data-testid="contacts-empty-state">
                        {isFiltering ? (
                            <p className="text-sm text-muted-foreground">No contacts match your search.</p>
                        ) : (
                            <>
                                <p className="text-sm text-muted-foreground mb-3">No contacts yet.</p>
                                <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Create your first contact</Button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                    <th className="text-left px-4 py-3">Name</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Company</th>
                                    <th className="text-left px-4 py-3">Mandate</th>
                                    <th className="text-left px-4 py-3">Warmth / Referrals</th>
                                    <th className="text-left px-4 py-3">Tags</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c, i) => (
                                    <motion.tr
                                        key={c.id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.25, ease: "easeOut" }}
                                        whileHover={{ backgroundColor: "rgba(245,245,245,0.6)" }}
                                        className="border-b border-border/60 cursor-pointer"
                                        onClick={() => navigate(`/contacts/${c.id}`)}
                                        data-testid={`contact-row-${c.id}`}
                                    >
                                        <td className="px-4 py-3">
                                            <Link to={`/contacts/${c.id}`} className="font-medium hover:text-gold transition-colors" data-testid={`open-contact-${c.id}`}>
                                                {c.name}
                                            </Link>
                                            {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <Badge variant="outline" className="capitalize text-[11px] font-medium rounded-[4px] px-2 py-0.5">{c.type}</Badge>
                                                {c.pe_backed && c.type === "buyer" && (
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-navy/10 text-navy border border-navy/30 rounded-sm font-mono font-bold" data-testid={`pe-badge-${c.id}`}>PE</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">{c.company || "—"}</td>
                                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                                            {c.mandate_sectors?.length ? c.mandate_sectors.join(", ") : "—"}
                                            {(c.mandate_ebitda_min || c.mandate_ebitda_max) && (
                                                <div>EBITDA {c.mandate_ebitda_min || 0}–{c.mandate_ebitda_max || "∞"}</div>
                                            )}
                                            {(c.ticket_size_min || c.ticket_size_max) && (
                                                <div className="text-gold">Ticket {c.ticket_size_min || 0}–{c.ticket_size_max || "∞"}</div>
                                            )}
                                            {c.past_acquisitions?.length > 0 && (
                                                <div className="text-[10px] mt-0.5">{c.past_acquisitions.length} past deal{c.past_acquisitions.length > 1 ? "s" : ""}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <WarmthReferralCell
                                                contact={c}
                                                warmth={warmthScores[c.id]}
                                                referral={referralStats[c.id]}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {(c.tags || []).map((t, i) => (
                                                    <span key={i} className="text-[10px] px-1.5 py-0.5 border border-border rounded-sm bg-muted/30 font-mono">{t}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`delete-contact-${c.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                            </Button>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const WARMTH_STYLE = {
    hot:     "bg-red-50 text-red-700 border-red-200",
    warm:    "bg-amber-50 text-amber-700 border-amber-200",
    warming: "bg-yellow-50 text-yellow-700 border-yellow-200",
    cold:    "bg-blue-50 text-blue-600 border-blue-200",
};

function WarmthReferralCell({ contact, warmth, referral }) {
    if (contact.type === "buyer" || contact.type === "investor") {
        if (!warmth || warmth.insufficient_data) return (
            <span className="text-[10px] text-muted-foreground" data-testid="warmth-no-data">—</span>
        );
        return (
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${WARMTH_STYLE[warmth.band] || ""}`} data-testid="warmth-badge">
                {warmth.band} · {warmth.score}
            </span>
        );
    }
    if (referral) return (
        <div className="text-xs" data-testid="referral-stats">
            <span className="font-medium">{referral.deals_referred}</span>
            <span className="text-muted-foreground"> ref · </span>
            <span className="font-medium">{referral.completion_rate}%</span>
            <span className="text-muted-foreground"> comp.</span>
        </div>
    );
    return <span className="text-[10px] text-muted-foreground">—</span>;
}

function NewContactDialog({ onCreated }) {
    const [form, setForm] = useState({
        name: "", type: "buyer", email: "", phone: "", company: "", role: "",
        tags: "", mandate_sectors: "", mandate_geographies: "",
        mandate_ebitda_min: "", mandate_ebitda_max: "",
        ticket_size_min: "", ticket_size_max: "",
        past_acquisitions: "", relationship_owner: "",
        notes: "",
    });
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
                mandate_sectors: form.mandate_sectors ? form.mandate_sectors.split(",").map((s) => s.trim()).filter(Boolean) : [],
                mandate_geographies: form.mandate_geographies ? form.mandate_geographies.split(",").map((s) => s.trim()).filter(Boolean) : [],
                past_acquisitions: form.past_acquisitions ? form.past_acquisitions.split(",").map((s) => s.trim()).filter(Boolean) : [],
                mandate_ebitda_min: form.mandate_ebitda_min ? parseFloat(form.mandate_ebitda_min) : null,
                mandate_ebitda_max: form.mandate_ebitda_max ? parseFloat(form.mandate_ebitda_max) : null,
                ticket_size_min: form.ticket_size_min ? parseFloat(form.ticket_size_min) : null,
                ticket_size_max: form.ticket_size_max ? parseFloat(form.ticket_size_max) : null,
                relationship_owner: form.relationship_owner || null,
            };
            await api.post("/contacts", payload);
            toast.success("Contact created");
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-contact-dialog">
            <DialogHeader><DialogTitle className="font-display">Create contact</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
                        <Input required value={form.name} onChange={setF("name")} data-testid="contact-name-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
                        <Select value={form.type} onValueChange={setF("type")}>
                            <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                        <Input type="email" value={form.email} onChange={setF("email")} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                        <Input value={form.phone} onChange={setF("phone")} />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company</Label>
                        <Input value={form.company} onChange={setF("company")} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                        <Input value={form.role} onChange={setF("role")} />
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Tag className="w-3 h-3" /> Tags (comma separated)</Label>
                    <Input value={form.tags} onChange={setF("tags")} placeholder="PE, Family Office, Strategic Buyer" />
                </div>
                {form.type === "buyer" && (
                    <div className="pt-2 mt-2 border-t border-border space-y-3">
                        <div className="text-xs uppercase tracking-wider text-foreground font-medium">Buyer mandate</div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Sectors (comma separated)</Label>
                            <Input value={form.mandate_sectors} onChange={setF("mandate_sectors")} placeholder="Healthcare, SaaS, Industrials" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Geographies</Label>
                            <Input value={form.mandate_geographies} onChange={setF("mandate_geographies")} placeholder="UK, Germany, Nordics" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA min</Label>
                                <Input type="number" className="font-mono" value={form.mandate_ebitda_min} onChange={setF("mandate_ebitda_min")} />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">EBITDA max</Label>
                                <Input type="number" className="font-mono" value={form.mandate_ebitda_max} onChange={setF("mandate_ebitda_max")} />
                            </div>
                        </div>
                    </div>
                )}
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                    <Textarea rows={2} value={form.notes} onChange={setF("notes")} />
                </div>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="submit-new-contact-btn">
                        {loading ? "Creating…" : "Create contact"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
